<template>
  <form @submit.prevent="handleSubmit" class="w-full flex flex-col gap-0 md:grid md:grid-cols-2">
    <ComboBox
      :items="brands"
      :placeholder="t('components.quick-search.brand')"
      :label="t('components.quick-search.brand')"
      v-model="brand"
      name="brand"
      inputClass="md:!rounded-tr-none md:!border-r-0"
      @update:modelValue="updateFilters"
    />

    <ComboBox
      :items="models"
      :label="t('components.quick-search.model')"
      :placeholder="!brand ? t('components.quick-search.model-not-allowed') : t('components.quick-search.model')"
      :disabled="!brand"
      v-model="model"
      name="model"
      inputClass="!rounded-none !border-t-0 md:!border-t md:!rounded-tr-sm"
      @update:modelValue="updateFilters"
    />

    <ComboBox
      :items="fuelTypes"
      :label="t('components.quick-search.fuel')"
      :placeholder="t('components.quick-search.fuel')"
      v-model="fuel"
      name="fuel"
      inputClass="!rounded-none !border-t-0 md:!border-r-0"
      @update:modelValue="updateFilters"
    />

    <ComboBox
      :items="transmissionTypes"
      :label="t('components.quick-search.transmission')"
      :placeholder="t('components.quick-search.transmission')"
      v-model="transmission"
      name="transmission"
      inputClass="!rounded-none !border-t-0 "
      @update:modelValue="updateFilters"
    />

    <SharedInput
      :label="t('components.quick-search.id')"
      hideLabel
      :placeholder="t('components.quick-search.id')"
      v-model="id"
      name="id"
      inputClass="!rounded-none !border-t-0 placeholder:text-regular-normal md:!border-r-0 md:!rounded-bl-sm"
      id="spotlight-search-vehicle-id"
      type="text"
    >
      <template #suffix>
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M10 18C11.775 17.9996 13.4988 17.4054 14.897 16.312L19.293 20.708L20.707 19.294L16.311 14.898C17.405 13.4997 17.9996 11.7754 18 10C18 5.589 14.411 2 10 2C5.589 2 2 5.589 2 10C2 14.411 5.589 18 10 18ZM10 4C13.309 4 16 6.691 16 10C16 13.309 13.309 16 10 16C6.691 16 4 13.309 4 10C4 6.691 6.691 4 10 4Z"
            fill="#1A847B"
          />
        </svg>
      </template>
    </SharedInput>

    <SharedButton
      variant="accent"
      type="submit"
      :label="searchLabel"
      class="!rounded-t-none !border-primary !border-t-0 md:!rounded-none md:!rounded-br-sm"
    ></SharedButton>
  </form>
</template>

<script lang="ts" setup>
  import type { QuickFiltersQueryResult } from '~/types/api';

  const { t } = useI18n();

  import ComboBox from './ComboBox.vue';

  const brand = ref<string>('');
  const model = ref<string>('');
  const fuel = ref<string>('');
  const transmission = ref<string>('');
  const id = ref<string>('');
  const newSum = ref<number>(0);

  // Hardcoded clustering ranges (always the same)
  // INFO: no clustering range in the current UI
  const clusteringRanges = {
    // mileage_ranges: [
    //   { name: '0-10.000 km', min: 0, max: 10000, slug: '0-10k' },
    //   { name: '10.000-50.000 km', min: 10000, max: 50000, slug: '10k-50k' },
    //   { name: '50.000-100.000 km', min: 50000, max: 100000, slug: '50k-100k' },
    //   { name: '100.000-150.000 km', min: 100000, max: 150000, slug: '100k-150k' },
    //   { name: '150.000+ km', min: 150000, max: null, slug: '150k-plus' },
    // ],
    // price_ranges: [
    //   { name: '0-10.000 €', min: 0, max: 10000, slug: '0-10k' },
    //   { name: '10.000-25.000 €', min: 10000, max: 25000, slug: '10k-25k' },
    //   { name: '25.000-50.000 €', min: 25000, max: 50000, slug: '25k-50k' },
    //   { name: '50.000-75.000 €', min: 50000, max: 75000, slug: '50k-75k' },
    //   { name: '75.000-100.000 €', min: 75000, max: 100000, slug: '75k-100k' },
    //   { name: '100.000+ €', min: 100000, max: null, slug: '100k-plus' },
    // ],
    // power_hp_ranges: [
    //   { name: '0-100 PS', min: 0, max: 100, slug: '0-100hp' },
    //   { name: '100-150 PS', min: 100, max: 150, slug: '100-150hp' },
    //   { name: '150-200 PS', min: 150, max: 200, slug: '150-200hp' },
    //   { name: '200-250 PS', min: 200, max: 250, slug: '200-250hp' },
    //   { name: '250-300 PS', min: 250, max: 300, slug: '250-300hp' },
    //   { name: '300+ PS', min: 300, max: null, slug: '300hp-plus' },
    // ],
  };

  // Dynamic data from API
  const brands = ref<Array<{ name: string; count: number; slug: string; disabled?: boolean }>>([]);
  const models = ref<Array<{ name: string; count: number; slug: string; disabled?: boolean }>>([]);
  const fuelTypes = ref<Array<{ name: string; count: number; slug: string; disabled?: boolean }>>([]);
  const transmissionTypes = ref<Array<{ name: string; count: number; slug: string; disabled?: boolean }>>([]);

  const { locale } = useI18n();
  // Fetch initial data
  const {
    data: initialFilters,
    error,
    pending,
  } = await useAsyncData(
    `initial-quick-filters-${locale.value}`,
    () =>
      $fetch('/api/vehicles/quick-filters', {
        query: {
          locale: locale.value,
          filters: clusteringRanges,
        },
      }),
    {
      watch: [locale],
    }
  );

  // Initialize sum with the initial value to avoid hydration mismatch
  const sum = ref<number>(initialFilters.value?.sum ?? 0);

  // Update local data with API response
  const updateLocalData = (filters: QuickFiltersQueryResult) => {
    newSum.value = filters.sum;
    brands.value = filters.make ||initialFilters.value?.make || [{ name: 'All brands', count: 0, slug: '__clear__', disabled: false }];
    // if (filters.make && filters.make.length > 0) {
    //   brands.value = filters.make;
    // } else {
    //   // Always show at least one option to allow clearing
    //   brands.value = [{ name: 'All brands', count: 0, slug: '__clear__', disabled: false }];
    // }

    if (filters.model && filters.model.length > 0) {
      models.value = filters.model;
    } else {
      // Always show at least one option to allow clearing
      models.value = [{ name: 'All models', count: 0, slug: '__clear__', disabled: false }];
    }

    if (filters.fuel_type && filters.fuel_type.length > 0) {
      fuelTypes.value = filters.fuel_type;
    } else {
      fuelTypes.value = [{ name: 'All fuel types', count: 0, slug: '__clear__', disabled: false }];
    }

    if (filters.transmission_type && filters.transmission_type.length > 0) {
      transmissionTypes.value = filters.transmission_type;
    } else {
      transmissionTypes.value = [{ name: 'All transmission types', count: 0, slug: '__clear__', disabled: false }];
    }
  };

  // Update filters when any selection changes
  const updateFilters = async () => {
    const currentFilters: any = {};

    // Add selected values to filters
    if (brand.value) currentFilters.make = brand.value;
    if (model.value) currentFilters.model = model.value;
    if (fuel.value) currentFilters.fuel_type = fuel.value;
    if (transmission.value) currentFilters.transmission_type = transmission.value;
    if (id.value) currentFilters.id = id.value;

    // Always include clustering ranges
    Object.assign(currentFilters, clusteringRanges);

    // Fetch updated filters

    try {
      const updatedFilters = await $fetch('/api/vehicles/quick-filters', {
        query: {
          locale: locale.value,
          filters: currentFilters,
        },
      });

      updateLocalData(updatedFilters);
    } catch (error) {
      console.error('Error fetching quick filters:', error);
    }
  };

  // Watch for brand changes to reset model selection
  watch(brand, newBrand => {
    if (newBrand !== model.value) {
      model.value = ''; // Reset model when brand changes
    }
    // Also reset model when brand is cleared
    if (!newBrand) {
      model.value = '';
    }

    // Only trigger filter update if we're not in the middle of another update
    if (!pending.value) {
      updateFilters();
    }
  });

  watch(newSum, (newValue, oldValue) => {
    // Skip animation during SSR - set value directly
    if (import.meta.server) {
      sum.value = newValue;
      return;
    }

    // Skip animation if values are the same (e.g., initial load)
    if (sum.value === newValue) {
      return;
    }

    const startValue = oldValue ?? sum.value;
    const endValue = newValue;
    const duration = 1500; // 1.5 seconds
    const startTime = Date.now();

    // Ease-out cubic function for smooth deceleration
    const easeOutCubic = (t: number): number => {
      return 1 - Math.pow(1 - t, 3);
    };

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutCubic(progress);
      
      sum.value = Math.round(startValue + (endValue - startValue) * easedProgress);

      if (progress < 1) {
        window.requestAnimationFrame(animate);
      } else {
        sum.value = endValue; // Ensure exact final value
      }
    };

    animate();
  });

  // Initialize with initial data
  if (initialFilters.value) {
    updateLocalData(initialFilters.value);
  } else {
    // Fallback to basic data if API fails
    brands.value = [
      { name: 'BMW', count: 0, slug: 'bmw', disabled: false },
      { name: 'Mercedes-Benz', count: 0, slug: 'mercedes', disabled: false },
      { name: 'Audi', count: 0, slug: 'audi', disabled: false },
    ];
  }

  const handleSubmit = () => {
    const queryParams: Record<string, string> = {};

    // Add selected values to query params
    if (brand.value) queryParams.make = brand.value;
    if (model.value) queryParams.model = model.value; // This is already the slugified value from the backend
    if (fuel.value) queryParams.fuel_type = fuel.value;
    if (transmission.value) queryParams.transmission_type = transmission.value;
    if (id.value) queryParams.id = id.value;

    // Build query string
    const queryString = Object.keys(queryParams).length > 0 ? '?' + new URLSearchParams(queryParams).toString() : '';

    // Redirect to vehicle search page
    navigateTo(`/autos-kaufen${queryString}`);
  };

  const searchLabel = computed(() => {
    let label = t('components.quick-search.submit');
    if (!isNaN(sum.value)) {
      label = `${label} (${sum.value.toLocaleString()})`;
    }

    return label;
  });
</script>

<style></style>
